import { zodResolver } from '@hookform/resolvers/zod';
import { UploadIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useScrapingJobs } from '../../hooks/use-scraping-jobs';

import { SOURCE_PLATFORM_TYPE } from '@/entities/public-filter/constants';
import { internalApiClient } from '@/shared/api-client/internal-api-client';
import { Button } from '@/shared/components/shadcn/button';
import { Checkbox } from '@/shared/components/shadcn/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/shadcn/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/shadcn/form';
import { Textarea } from '@/shared/components/shadcn/textarea';
import { cn } from '@/shared/utils/cn';
import { showErrorDialog } from '@/shared/utils/show-error-dialog';

const FormSchema = z.object({
  platform: z.enum(SOURCE_PLATFORM_TYPE),
  csvFile: z.instanceof(File, { message: 'Please select a CSV file with URLs.' }),
  shouldScrapeComments: z.boolean(),
  forceRescrape: z.boolean(),
  memo: z.string().optional(),
});

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
  onConfirm: () => void;
}

// 100MB in bytes
const MAX_FILE_SIZE = 100 * 1024 * 1024;

async function createDirectUrlScrapingJob(data: {
  platform: SOURCE_PLATFORM_TYPE;
  csvFile: File;
  shouldScrapeComments: boolean;
  forceRescrape: boolean;
  memo?: string;
}) {
  const formData = new FormData();
  formData.append('platform', data.platform);
  formData.append('csvFile', data.csvFile);
  formData.append('shouldScrapeComments', data.shouldScrapeComments.toString());
  formData.append('forceRescrape', data.forceRescrape.toString());
  if (data.memo) {
    formData.append('memo', data.memo);
  }

  try {
    return await internalApiClient.post('social-post-scraping-jobs/scrape-by-urls', {
      body: formData,
    });
  } catch (error) {
    console.error('Failed to create URL scraping job:', error);
    throw error;
  }
}

export function DirectUrlScrapingDialog({ isOpen, onClose, onExit, onConfirm }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      platform: SOURCE_PLATFORM_TYPE.TIKTOK,
      shouldScrapeComments: false,
      forceRescrape: false,
      memo: '',
    },
  });

  const selectedFile = form.watch('csvFile');

  const handleFileSelect = (file: File) => {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      form.setError('csvFile', {
        type: 'manual',
        message: `File size must be less than 100MB. Your file is ${(file.size / (1024 * 1024)).toFixed(1)}MB.`,
      });
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    form.setValue('csvFile', file);
    form.clearErrors('csvFile');
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragActive(false);

    const file = event.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setDragActive(false);
  };

  const handleSubmit = async (data: z.infer<typeof FormSchema>) => {
    try {
      await createDirectUrlScrapingJob({
        platform: data.platform,
        csvFile: data.csvFile,
        shouldScrapeComments: data.shouldScrapeComments,
        forceRescrape: data.forceRescrape,
        memo: data.memo,
      });
      await useScrapingJobs.refetch('DIRECT_URL');
      onConfirm();
      onClose();
      onExit();
      form.reset();
    } catch (error) {
      await showErrorDialog({ error });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-100">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <DialogHeader>
              <DialogTitle>Create URL scraping job</DialogTitle>
              <DialogDescription>
                Upload a CSV file with 'postUrls' column containing TikTok URLs to scrape
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-4">
              <FormField
                control={form.control}
                name="csvFile"
                render={({ field: { onChange: _onChange, onBlur, name, ref: _ref } }) => (
                  <FormItem>
                    <FormLabel>Upload CSV file</FormLabel>
                    <FormControl>
                      <div>
                        <input
                          type="file"
                          accept=".csv"
                          onChange={handleFileInputChange}
                          className="hidden"
                          ref={fileInputRef}
                          name={name}
                          onBlur={onBlur}
                        />
                        <div
                          className={cn(
                            'cursor-pointer rounded-lg border-2 border-dashed p-6 transition-colors',
                            dragActive
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-zinc-300 hover:border-zinc-400',
                          )}
                          onDrop={handleDrop}
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <div className="text-center">
                            <UploadIcon className="mx-auto h-12 w-12 text-zinc-400" />
                            <div className="mt-4">
                              {selectedFile ? (
                                <div>
                                  <p className="text-sm font-medium text-zinc-900">
                                    {selectedFile.name}
                                  </p>
                                  <p className="text-xs text-zinc-500">
                                    {(selectedFile.size / 1024).toFixed(1)} KB
                                  </p>
                                </div>
                              ) : (
                                <div>
                                  <p className="text-sm text-zinc-600">
                                    <span className="font-medium">Click to upload</span> or drag and
                                    drop
                                  </p>
                                  <p className="text-xs text-zinc-500">
                                    CSV file with 'postUrls' column (max 100MB)
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* TODO: 필요해지면 공개 */}
              {/* <FormField
                control={form.control}
                name="shouldScrapeComments"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-y-0 space-x-3">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Scrape comments</FormLabel>
                      <p className="text-muted-foreground text-xs">
                        Also collect comments from the posts (additional cost applies)
                      </p>
                    </div>
                  </FormItem>
                )}
              /> */}

              <FormField
                control={form.control}
                name="forceRescrape"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-y-0 space-x-3">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Force re-scrape</FormLabel>
                      <p className="text-muted-foreground text-xs">
                        Re-scrape posts even if they were previously collected
                      </p>
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="memo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Memo (Optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Add any notes about this scraping job..."
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button
                variant="default"
                type="submit"
                disabled={!selectedFile || form.formState.isSubmitting}
              >
                Create
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
